// What the two voice-write triggers can REFUSE with, recognised in one place.
//
// ── Why this moved out of src/lib/voiceWriteBudget.ts (#1694) ────────────────
//
// It lived in `src/lib` because voice's first three writes are browser-side RPC
// calls, and the browser was the only place that had to interpret a refusal.
// #1686 gave voice a FOURTH write that goes through `kin-chat/createEventWrite.ts`
// — an Edge module, which cannot import from `src/`. So recognising the same two
// database outcomes from that module meant either a second copy of these
// predicates or one shared definition, and three hand-synced copies of one rule
// is this codebase's most common defect shape (#361, #470, #515, #1685).
//
// It lives HERE rather than in `src/lib` because the dependency only points one
// way: `src/` already imports Edge modules directly (entitlementActive.ts,
// upcomingTaskReminders.ts, voiceScheduleRead.ts), and nothing under
// `supabase/functions` may import from `src`. `voiceWriteBudget.ts` re-exports
// every name below, so all the existing callers are untouched.
//
// ⚠️ Nothing in this file enforces anything. The caps are enforced by BEFORE
// INSERT triggers in Postgres (20260816120000, 20260829090000, 20260924090000);
// this only reads what they raise. A client cannot cap itself, which is the whole
// point of #933.
//
// ⚠️ A misrecognised refusal is worse than no cap at all: it reaches the model as
// "couldn't save that", which is an invitation to retry, which is the loop the
// cap exists to break.

/**
 * PostgREST maps a SQLSTATE of the form `PTxyz` to HTTP status `xyz`, so the
 * budget triggers surface as a 429 with this code rather than an opaque 500.
 */
export const VOICE_RATE_LIMIT_CODE = "PT429";

/**
 * Marker embedded in the exception message by every budget trigger.
 *
 * ⚠️ Matched IN ADDITION to the code, not instead of it. Either one alone is
 * enough to recognise the refusal; carrying both means a PostgREST upgrade that
 * changed how custom SQLSTATEs surface would degrade the detection rather than
 * break it.
 */
export const VOICE_RATE_LIMIT_MARKER = "voice_write_rate_limited";

/**
 * Unique indexes that mean "this propose id already wrote a row."
 *
 * One entry per voice write destination. A MISSING entry is the dangerous state:
 * the collision is then read as a generic failure and the model retries a write
 * that actually succeeded.
 */
export const PROPOSE_INDEXES = [
  "family_context_propose_uidx",
  "task_queue_items_propose_uidx",
  // #1159 — voice's third write path.
  "todo_list_items_propose_uidx",
  // #1694 — the fourth, and the only one whose table is also written by chat,
  // by Google/Apple import, by photo import, by the party mirror and by the
  // Today tab. `voiceEventBudget.test.ts` pins this name against the migration
  // that creates the index, because a typo here is invisible: it degrades to
  // "couldn't save that" over a row that exists.
  "events_propose_uidx",
];

/** Postgres unique_violation. */
export const UNIQUE_VIOLATION = "23505";

interface DbErrorish {
  code?: string | null;
  message?: string | null;
  details?: string | null;
  hint?: string | null;
}

function haystack(err: unknown): string {
  const e = (err ?? {}) as DbErrorish;
  return [e.message, e.details, e.hint].filter(Boolean).join(" ").toLowerCase();
}

function codeOf(err: unknown): string {
  const e = (err ?? {}) as DbErrorish;
  return String(e.code ?? "");
}

/** True when the per-window cap refused this write. */
export function isVoiceRateLimitError(err: unknown): boolean {
  if (!err) return false;
  if (codeOf(err) === VOICE_RATE_LIMIT_CODE) return true;
  return haystack(err).includes(VOICE_RATE_LIMIT_MARKER);
}

/**
 * True when this write lost a race to an earlier call carrying the same propose
 * id — i.e. the row it wanted is already there.
 *
 * ⚠️ This is a SUCCESS, not a failure, and the caller must treat it as one. The
 * proposal was honored; a second row is what we were preventing. Reporting it
 * as an error would make Kin apologise for having already done the thing.
 */
export function isDuplicateProposeError(err: unknown): boolean {
  if (!err) return false;
  if (codeOf(err) !== UNIQUE_VIOLATION) return false;
  const text = haystack(err);
  return PROPOSE_INDEXES.some((idx) => text.includes(idx));
}
